import React, { useState } from 'react';
import { ChevronDown, X, Menu } from 'lucide-react';
import 'flag-icons/css/flag-icons.min.css';

type Language = 'en' | 'fr' | 'ar';

interface NavbarProps {
  currentLang: Language;
  onLanguageChange: (lang: Language) => void;
}

const translations = {
  en: {
    nav: { features: 'Features', benefits: 'Benefits', pricing: 'Pricing', login: 'Log in', getStarted: 'Get Started' },
  },
  fr: {
    nav: { features: 'Fonctionnalités', benefits: 'Avantages', pricing: 'Tarifs', login: 'Connexion', getStarted: 'Commencer' },
  },
  ar: {
    nav: { features: 'المميزات', benefits: 'الفوائد', pricing: 'الأسعار', login: 'تسجيل الدخول', getStarted: 'ابدأ الآن' },
  }
};

const Navbar: React.FC<NavbarProps> = ({ currentLang, onLanguageChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  
  const t = translations[currentLang];
  const isRTL = currentLang === 'ar';
  
  const langLabels: Record<Language, { flagClass: string; name: string; short: string }> = {
    en: { flagClass: 'fi fi-us', name: 'English', short: 'EN' },
    fr: { flagClass: 'fi fi-fr', name: 'Français', short: 'FR' },
    ar: { flagClass: 'fi fi-tn', name: 'العربية', short: 'عربي' }
  };

  return (
    <nav className="sticky top-0 bg-white/95 backdrop-blur-lg z-50 border-b border-slate-100">
      <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
        <a href="/" className="text-4xl md:text-5xl" style={{ fontFamily: 'Caveat, cursive', color: '#273B7D', fontWeight: 700, letterSpacing: '-0.02em' }}>Karia</a>
        
        <div className="hidden md:flex items-center gap-12" style={{ fontFamily: 'Inter, sans-serif' }}>
          <a href="/#features" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.features}</a>
          <a href="/#benefits" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.benefits}</a>
          <a href="/#pricing" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.pricing}</a>
          <a href="/about" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">About</a>
          
          <div className="flex items-center gap-4 ml-6">
            <a 
              href="https://app.karia.tn/login" 
              className="text-base font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-slate-100" 
              style={{ color: '#273B7D' }}
            >
              {t.nav.login}
            </a>
            <a 
              href="https://app.karia.tn/demo?code=mdshamal" 
              className="text-base font-semibold text-white px-7 py-3 rounded-lg transition-all hover:opacity-90" 
              style={{ backgroundColor: '#FF690F' }}
            >
              See Live Demo
            </a>
            
            {/* Language Switcher */}
            <div className="relative">
              <button 
                onClick={() => setLangMenuOpen(!langMenuOpen)} 
                className="flex items-center gap-2 text-base font-medium text-slate-600 hover:text-slate-900 transition-colors px-4 py-2.5 rounded-full hover:bg-slate-100 border border-slate-200"
              >
                <span className={langLabels[currentLang].flagClass} style={{ fontSize: '1.25rem' }}></span>
                <span>{langLabels[currentLang].name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {langMenuOpen && (
                <div className="absolute top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-3 min-w-[160px]" style={{ right: isRTL ? 'auto' : 0, left: isRTL ? 0 : 'auto' }}>
                  {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                    <button 
                      key={l} 
                      onClick={() => { onLanguageChange(l); setLangMenuOpen(false); }} 
                      className={`w-full px-4 py-3 text-base text-left hover:bg-slate-50 transition-colors flex items-center gap-3 ${currentLang === l ? 'font-semibold bg-slate-50' : ''}`} 
                      style={{ textAlign: isRTL ? 'right' : 'left' }}
                    >
                      <span className={langLabels[l].flagClass} style={{ fontSize: '1.25rem' }}></span>
                      <span>{langLabels[l].name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <button 
          className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white shadow-lg">
          <div className="px-4 py-6 space-y-4">
            <a href="/#features" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.features}</a>
            <a href="/#benefits" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.benefits}</a>
            <a href="/#pricing" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.pricing}</a>
            <a href="/about" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">About</a>
            
            {/* Mobile Language Switcher */}
            <div className="flex gap-2 py-2">
              {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                <button 
                  key={l} 
                  onClick={() => onLanguageChange(l)} 
                  className={`px-4 py-2.5 text-sm rounded-lg border transition-colors flex items-center gap-2 ${currentLang === l ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}
                >
                  <span className={langLabels[l].flagClass} style={{ fontSize: '1rem' }}></span>
                  <span>{langLabels[l].short}</span>
                </button>
              ))}
            </div>
            
            <div className="pt-4 border-t border-slate-100 space-y-3">
              <a 
                href="https://app.karia.tn/login" 
                className="block text-center py-2.5 font-medium rounded-lg border border-slate-200" 
                style={{ color: '#273B7D' }}
              >
                {t.nav.login}
              </a>
              <a 
                href="https://app.karia.tn/demo?code=mdshamal" 
                className="block w-full text-center text-white font-semibold px-5 py-2.5 rounded-lg transition-all" 
                style={{ backgroundColor: '#FF690F' }}
              >
                See Live Demo
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;